import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, useResource } from "@/lib/api";
import { PageHeader, inputCls, btnPrimary, cardCls, EmptyState, ColorPicker } from "@/components/shared";
import { TodoPanel } from "@/components/todo/TodoPanel";
import { ProgressRing, LIST_ICONS } from "@/components/todo/ListIcon";
import { IconPicker } from "@/components/todo/IconPicker";

const NewListForm = ({ onCreate }) => {
  const [name, setName] = useState("");
  const [color, setColor] = useState("terracotta");
  const [icon, setIcon] = useState("folder");
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    await onCreate({ name: name.trim(), color, icon });
    setName("");
    setIcon("folder");
  };
  const { Icon } = LIST_ICONS[icon];
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="new-todo-list-form">
      <div className="flex gap-2">
        <IconPicker value={icon} onPick={setIcon} testId="new-todo-list-icon-picker">
          <button type="button" data-testid="new-todo-list-icon-button" title="Choose an icon"
            className="h-[42px] w-[42px] shrink-0 rounded-xl border border-t-line bg-t-card flex items-center justify-center text-t-c4 hover:bg-t-surface transition-colors">
            <Icon className="h-5 w-5" />
          </button>
        </IconPicker>
        <input
        data-testid="new-todo-list-name-input"
        className={inputCls}
        placeholder="New list, e.g. Weekend Chores"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      </div>
      <div className="flex items-center justify-between gap-3">
        <ColorPicker value={color} onChange={setColor} testPrefix="new-todo-list" />
        <button type="submit" data-testid="create-todo-list-button" className={btnPrimary}>
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
    </form>
  );
};

export default function Todos() {
  const lists = useResource("/todo-lists");
  const shops = useResource("/shop-lists");
  const [params] = useSearchParams();
  const [activeId, setActiveId] = useState(params.get("list"));

  useEffect(() => {
    if (!lists.data.find((l) => l.id === activeId) && lists.data.length) setActiveId(lists.data[0].id);
  }, [lists.data, activeId]);

  const createList = async (body) => {
    const r = await api.post("/todo-lists", body);
    lists.setData((d) => [...d, r.data]);
    setActiveId(r.data.id);
    toast.success("List created");
  };
  const deleteList = async (id) => {
    await api.delete(`/todo-lists/${id}`);
    lists.setData((d) => d.filter((l) => l.id !== id));
    shops.reload();
    toast("List removed");
  };
  const setIcon = async (l, icon) => lists.replace((await api.patch(`/todo-lists/${l.id}`, { icon })).data);
  const active = lists.data.find((l) => l.id === activeId);

  return (
    <div data-testid="todos-page">
      <PageHeader eyebrow="Home & Life" title="To-Do Lists" subtitle="Separate lists for every corner of your life. Tick things off, one gentle step at a time." />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 space-y-6 fade-up">
          <div className={`${cardCls} p-6`}>
            <NewListForm onCreate={createList} />
          </div>
          <div className="space-y-2" data-testid="todo-lists-sidebar">
            {lists.data.map((l) => {
              const done = l.items.filter((i) => i.done).length;
              return (
                <div
                  key={l.id}
                  onClick={() => setActiveId(l.id)}
                  data-testid={`todo-list-tab-${l.id}`}
                  className={`group flex items-center gap-3 p-4 rounded-2xl border cursor-pointer transition-[background-color,border-color] duration-200 ${
                    l.id === activeId ? "bg-t-card border-t-primary/50 shadow-sm" : "bg-transparent border-t-line hover:bg-t-card/70"
                  }`}
                >
                  <IconPicker value={l.icon || "folder"} onPick={(icon) => setIcon(l, icon)} testId={`todo-list-icon-picker-${l.id}`}>
                    <button type="button" onClick={(e) => e.stopPropagation()} data-testid={`todo-list-icon-${l.id}`} title="Change icon"
                      className="rounded-full transition-transform hover:scale-105">
                      <ProgressRing list={l} testId={`todo-list-ring-${l.id}`} />
                    </button>
                  </IconPicker>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{l.name}</p>
                    <p className="text-xs text-t-muted">{done}/{l.items.length} done</p>
                  </div>
                  <button
                    data-testid={`delete-todo-list-${l.id}`}
                    onClick={(e) => { e.stopPropagation(); deleteList(l.id); }}
                    className="opacity-0 group-hover:opacity-100 p-2 rounded-lg hover:bg-t-primary-soft text-t-danger transition-opacity"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
        <div className="lg:col-span-8">
          {active ? (
            <TodoPanel list={active} onChange={lists.replace} shops={shops} />
          ) : (
            !lists.loading && <EmptyState testId="todos-empty" title="No lists yet" text="Create your first to-do list to get started." />
          )}
        </div>
      </div>
    </div>
  );
}
